import { maskCnpj, maskCpf, onlyDigits, validCnpj, validCpf } from '@divipay/br-documents';

/**
 * As mesmas regras que a PagBank aplica no cadastro. Ela devolve tudo como 400
 * 'Invalid parameter', então sem isto o operador preenche o formulário inteiro
 * para descobrir no fim que o nome tinha um espaço a mais.
 *
 * O backend valida de novo — isto aqui é para não gastar a viagem.
 * https://developer.pagbank.com.br/reference/criar-conta
 */

const LETRA = /\p{L}/u;

// ---------------- máscaras ----------------

export { maskCpf, maskCnpj, onlyDigits };

/** (51) 99999-8888 — aceita fixo de 8 dígitos e celular de 9. */
export function maskPhone(valor: string): string {
  const d = onlyDigits(valor).slice(0, 11);
  if (d.length <= 2) return d;
  const ddd = `(${d.slice(0, 2)})`;
  const resto = d.slice(2);
  if (resto.length <= 4) return `${ddd} ${resto}`;
  const corte = resto.length > 8 ? 5 : 4;
  return `${ddd} ${resto.slice(0, corte)}-${resto.slice(corte)}`;
}

/** 90000-000 */
export function maskCep(valor: string): string {
  const d = onlyDigits(valor).slice(0, 8);
  return d.length <= 5 ? d : `${d.slice(0, 5)}-${d.slice(5)}`;
}

export function maskUf(valor: string): string {
  return valor.replace(/[^A-Za-z]/g, '').slice(0, 2).toUpperCase();
}

/** DDD e número, como a PagBank quer, a partir do campo mascarado. */
export function splitPhone(valor: string): { area: string; number: string } {
  const d = onlyDigits(valor);
  return { area: d.slice(0, 2), number: d.slice(2) };
}

// ---------------- validações ----------------

/**
 * 'Minimum of 2 words with a minimum of 2 letters each. It cannot start or end
 * with an empty space and special character or have consecutive empty spaces.'
 */
export function nomeCompletoInvalido(valor: string): string | null {
  const v = valor ?? '';
  if (!v.trim()) return 'Informe o nome completo';
  if (v !== v.trim()) return 'Sem espaços no início ou no fim';
  if (/\s{2,}/.test(v)) return 'Sem espaços duplicados';
  if (!LETRA.test(v.charAt(0)) || !LETRA.test(v.charAt(v.length - 1))) {
    return 'Não pode começar nem terminar com pontuação';
  }
  const palavras = v.split(' ').filter((p) => (p.match(/\p{L}/gu) ?? []).length >= 2);
  if (palavras.length < 2) return 'Informe nome e sobrenome (duas palavras de duas letras)';
  return null;
}

/** A PagBank exige titular com 18 anos ou mais. */
export function nascimentoInvalido(valor: string): string | null {
  if (!valor) return 'Informe a data de nascimento';
  const partes = /^(\d{4})-(\d{2})-(\d{2})$/.exec(valor);
  if (!partes) return 'Data inválida';

  const [ano, mes, dia] = partes.slice(1).map(Number);
  const nascimento = new Date(Date.UTC(ano, mes - 1, dia));
  if (
    nascimento.getUTCFullYear() !== ano ||
    nascimento.getUTCMonth() !== mes - 1 ||
    nascimento.getUTCDate() !== dia
  ) {
    return 'Data inválida';
  }

  const hoje = new Date();
  const limite = Date.UTC(hoje.getFullYear() - 18, hoje.getMonth(), hoje.getDate());
  if (nascimento.getTime() > limite) return 'O titular precisa ter pelo menos 18 anos';
  return null;
}

export function cpfInvalido(valor: string): string | null {
  if (!onlyDigits(valor)) return 'Informe o CPF';
  if (onlyDigits(valor).length !== 11) return 'O CPF tem 11 dígitos';
  return validCpf(valor) ? null : 'CPF inválido';
}

export function cnpjInvalido(valor: string): string | null {
  if (!onlyDigits(valor)) return 'Informe o CNPJ';
  if (onlyDigits(valor).length !== 14) return 'O CNPJ tem 14 dígitos';
  return validCnpj(valor) ? null : 'CNPJ inválido';
}

export function cepInvalido(valor: string): string | null {
  if (!onlyDigits(valor)) return 'Informe o CEP';
  return onlyDigits(valor).length === 8 ? null : 'O CEP tem 8 dígitos';
}

export function ufInvalida(valor: string): string | null {
  if (!valor) return 'Informe a UF';
  return /^[A-Za-z]{2}$/.test(valor) ? null : 'A UF tem 2 letras';
}

export function emailInvalido(valor: string): string | null {
  if (!valor) return 'Informe o e-mail';
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(valor) ? null : 'E-mail inválido';
}

/**
 * Celular, e não telefone qualquer: o payload manda `type: 'MOBILE'` fixo
 * (pagbank-account.client.ts), então a PagBank recusa fixo de 8 dígitos com
 * 40001 "must be ... (cell phone type)" depois do formulário inteiro preenchido.
 * Daí a regra ser DDD + 9 dígitos começando em 9, e não 10-ou-11.
 */
export function telefoneInvalido(valor: string): string | null {
  const d = onlyDigits(valor);
  if (!d) return 'Informe o telefone';
  if (d.length < 11) return 'Informe DDD e celular com 9 dígitos';
  if (d.length > 11) return 'Número muito longo';
  if (!/^[1-9][0-9]$/.test(d.slice(0, 2))) return 'DDD inválido';
  if (d[2] !== '9') return 'Precisa ser celular — o número começa com 9';
  return null;
}

export function obrigatorio(valor: string, rotulo: string): string | null {
  return valor && valor.trim() ? null : `Informe ${rotulo}`;
}
